import { useState, useMemo, useEffect } from "react";
import { usePageTitle } from "@/hooks/use-page-title";
import { useMutation, useQuery } from "@tanstack/react-query";
import type { CarrierWithRelations, Company, User } from "@shared/schema";
import { useRoute, useLocation, Link } from "wouter";
import { apiRequest, queryClient } from "@/lib/queryClient";
import ResponsiveLayout from "@/components/layout/responsive-layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import EditCarrierModal from "@/components/modals/edit-carrier-modal";
import CreateWorkOrderModal from "@/components/modals/create-work-order-modal";
import FMCSACarrierRefreshModal from "@/components/modals/fmcsa-carrier-refresh-modal";
import NotesSection from "@/components/notes/notes-section";
import ContactsSection from "@/components/contacts/contacts-section";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { 
  Truck, Mail, Phone, MapPin, Building2, Edit, FileText, ExternalLink, Search, 
  Calendar, Plus, AlertCircle, Clock, CheckCircle, XCircle, History, RefreshCw, Download
} from "lucide-react";
import { format } from "date-fns";
import { parseDateOnly, formatDateOnly } from "@/lib/dateUtils";
import AuditInfo from "@/components/audit-info";
import { track } from "@/lib/analytics";
import { formatPhoneNumber } from "@/lib/utils";

interface AuditUserInfo {
  id: string;
  firstName: string | null;
  lastName: string | null;
}

interface CarrierDetailResponse extends CarrierWithRelations {
  createdByUser: AuditUserInfo | null;
  updatedByUser: AuditUserInfo | null;
}

interface AuthorityHistoryEvent {
  docket_number: string;
  dot_number: string;
  mod_col_1: string;
  original_action_desc: string;
  orig_served_date: string;
  disp_action_desc?: string;
  disp_decided_date?: string;
  disp_served_date?: string;
}

export default function CarrierDetailsPage() {
  const { toast } = useToast();
  const { user: currentUser } = useAuth();
  const [, setLocation] = useLocation();
  const [match, params] = useRoute("/carriers/:id");
  const carrierId = params?.id ? parseInt(params.id) : null;
  
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateWorkOrderModal, setShowCreateWorkOrderModal] = useState(false);
  const [showFMCSARefreshModal, setShowFMCSARefreshModal] = useState(false);
  const [workOrderSearchTerm, setWorkOrderSearchTerm] = useState("");
  const [workOrderStatusFilter, setWorkOrderStatusFilter] = useState("all");
  
  const { data: carrier, isLoading } = useQuery<CarrierDetailResponse>({
    queryKey: ["/api/carriers", carrierId],
    enabled: !!carrierId,
    retry: false,
  });

  const { data: companies } = useQuery<Company[]>({
    queryKey: ["/api/companies"],
    retry: false,
    select: (d: any) => d?.data ?? (Array.isArray(d) ? d : []),
  });

  const companyId = carrier?.company?.id;
  const { data: companyContacts = [] } = useQuery<any[]>({
    queryKey: ["/api/companies", companyId, "contacts"],
    queryFn: async () => {
      const response = await fetch(`/api/companies/${companyId}/contacts`, {
        credentials: "include",
      });
      if (!response.ok) throw new Error("Failed to fetch contacts");
      return response.json();
    },
    enabled: !!companyId,
  });

  const { data: users = [] } = useQuery<User[]>({
    queryKey: ["/api/assignable-users"],
    enabled: !!carrierId,
  });

  const { data: authorityHistory = [], isLoading: isLoadingHistory } = useQuery<AuthorityHistoryEvent[]>({
    queryKey: ["/api/carriers", carrierId, "authority-history"],
    queryFn: async () => {
      const response = await fetch(`/api/carriers/${carrierId}/authority-history`, {
        credentials: "include",
      });
      if (!response.ok) return [];
      return response.json();
    },
    enabled: !!carrierId && !!carrier?.docketNumber,
  });

  const { data: authorityLetters = [] } = useQuery<any[]>({
    queryKey: ["/api/authority-letters", { carrierId }],
    queryFn: async () => {
      const response = await fetch(`/api/authority-letters?carrierId=${carrierId}`, {
        credentials: "include",
      });
      if (!response.ok) throw new Error("Failed to fetch authority letters");
      const body = await response.json();
      return body?.data ?? (Array.isArray(body) ? body : []);
    },
    enabled: !!carrierId,
  });

  // Find the primary contact for the carrier's company (uses isPrimary boolean only)
  const primaryContact = useMemo(() => {
    const primaryRelation = companyContacts.find((cc: any) => cc.isPrimary === true);
    return primaryRelation?.contact;
  }, [companyContacts]);

  const carrierPageTitle = (() => {
    if (!carrier) return null;
    const identifiers: string[] = [];
    if (carrier.docketPrefix && carrier.docketNumber) {
      identifiers.push(`Docket:${carrier.docketPrefix}${carrier.docketNumber}`);
    }
    if (carrier.company?.dotProfile?.dotNumber) identifiers.push(`DOT:${carrier.company.dotProfile.dotNumber}`);
    return identifiers.length > 0
      ? `${carrier.name} (${identifiers.join(' ')})`
      : carrier.name;
  })();
  usePageTitle(carrierPageTitle);

  useEffect(() => {
    if (showCreateWorkOrderModal) {
      track("work_order_create_opened", {
        source_page: `/carriers/${carrierId}`,
        carrier_id: carrierId,
      });
    }
  }, [showCreateWorkOrderModal, carrierId]);

  const updateCarrierMutation = useMutation({
    mutationFn: async (carrierData: any) => {
      await apiRequest("PUT", `/api/carriers/${carrierId}`, carrierData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/carriers"] });
      queryClient.invalidateQueries({ queryKey: ["/api/carriers", carrierId] });
      toast({
        title: "Success",
        description: "Carrier updated successfully",
      });
      setShowEditModal(false);
      setShowFMCSARefreshModal(false);
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update carrier",
        variant: "destructive",
      });
    },
  });

  const createWorkOrderMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await apiRequest("POST", "/api/work-orders", data);
      return await response.json();
    },
    onSuccess: (createdWorkOrder) => {
      track("work_order_created", {
        source_page: `/carriers/${carrierId}`,
        work_order_id: createdWorkOrder?.id ?? null,
        company_id: createdWorkOrder?.companyId ?? null,
        carrier_id: createdWorkOrder?.carrierId ?? carrierId,
      });
      queryClient.invalidateQueries({ queryKey: ["/api/carriers", carrierId] });
      queryClient.invalidateQueries({ queryKey: ["/api/work-orders"] });
      toast({
        title: "Success",
        description: "Work order created successfully",
      });
      setShowCreateWorkOrderModal(false);
      // Navigate to the newly created work order details page
      if (createdWorkOrder?.id) {
        setLocation(`/work-orders/${createdWorkOrder.id}`);
      }
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error?.message || "Failed to create work order",
        variant: "destructive",
      });
    },
  });

  const filteredWorkOrders = useMemo(() => {
    if (!carrier?.workOrders) return [];
    
    return carrier.workOrders.filter((workOrder: any) => {
      const matchesSearch = workOrderSearchTerm === "" || 
        workOrder.workOrderNumber?.toLowerCase().includes(workOrderSearchTerm.toLowerCase()) ||
        workOrder.serviceType?.toLowerCase().includes(workOrderSearchTerm.toLowerCase());
      
      const matchesStatus = workOrderStatusFilter === "all" || 
        (workOrderStatusFilter === "active" && workOrder.status !== "closed" && workOrder.status !== "cancelled") ||
        workOrder.status === workOrderStatusFilter;
      
      return matchesSearch && matchesStatus;
    });
  }, [carrier?.workOrders, workOrderSearchTerm, workOrderStatusFilter]);

  const sortedAuthorityHistory = useMemo(() => {
    if (!authorityHistory.length) return [];
    return [...authorityHistory].sort((a, b) => {
      const dateA = a.orig_served_date ? parseDateOnly(a.orig_served_date).getTime() : 0;
      const dateB = b.orig_served_date ? parseDateOnly(b.orig_served_date).getTime() : 0;
      return dateB - dateA;
    });
  }, [authorityHistory]);

  const handleViewProfile = (docketPrefix: string, docketNumber: string) => {
    if (docketNumber) {
      const profileUrl = `https://safer.fmcsa.dot.gov/query.asp?searchtype=ANY&query_type=queryCarrierSnapshot&query_param=MC_MX&query_string=${docketNumber}`;
      window.open(profileUrl, "_blank");
    }
  };

  const handleFMCSARefresh = (data: any) => {
    updateCarrierMutation.mutate({ ...data, lastFmcsaFetchedAt: new Date().toISOString() });
  };

  const getFMCSAStatusColor = (status: string | null | undefined) => {
    if (!status) return "bg-gray-100 text-gray-800";
    const upperStatus = status.toUpperCase();
    switch (upperStatus) {
      case "ACTIVE":
      case "A":
      case "AUTHORIZED":
        return "bg-green-100 text-green-800";
      case "PENDING":
      case "P":
        return "bg-yellow-100 text-yellow-800";
      case "SUSPENDED":
      case "S":
        return "bg-red-100 text-red-800";
      case "REVOKED":
      case "R":
      case "INACTIVE":
      case "NONE":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const formatFMCSAStatus = (status: string | null | undefined) => {
    if (!status) return "N/A";
    const upper = status.toUpperCase().trim();
    switch (upper) {
      case "A": return "Active";
      case "I": return "Inactive";
      case "N": return "None";
      case "P": return "Pending";
      case "R": return "Revoked";
      case "S": return "Suspended";
      case "ACTIVE": return "Active";
      case "INACTIVE": return "Inactive";
      case "NONE": return "None";
      case "PENDING": return "Pending";
      case "REVOKED": return "Revoked";
      case "SUSPENDED": return "Suspended";
      case "AUTHORIZED": return "Authorized";
      default:
        return status.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());
    }
  };

  const getActionIcon = (action: string) => {
    const upper = action?.toUpperCase() || "";
    if (upper.includes("GRANTED") || upper.includes("REINSTATED")) {
      return <CheckCircle className="w-4 h-4 text-green-600" />;
    }
    if (upper.includes("REVOK") || upper.includes("SUSPEND")) {
      return <XCircle className="w-4 h-4 text-red-600" />;
    }
    if (upper.includes("DISCONTINUE")) {
      return <CheckCircle className="w-4 h-4 text-blue-600" />;
    }
    return <Clock className="w-4 h-4 text-gray-600" />;
  };

  const getActionColor = (action: string) => {
    const upper = action?.toUpperCase() || "";
    if (upper.includes("GRANTED") || upper.includes("REINSTATED")) {
      return "bg-green-100 text-green-800";
    }
    if (upper.includes("REVOK") || upper.includes("SUSPEND")) {
      return "bg-red-100 text-red-800";
    }
    if (upper.includes("DISCONTINUE")) {
      return "bg-blue-100 text-blue-800";
    }
    return "bg-gray-100 text-gray-800";
  };

  if (isLoading) {
    return (
      <ResponsiveLayout title="Loading..." subtitle="Fetching carrier details" isLoading={true}>
        <div className="flex items-center justify-center py-16">
          <p className="text-gray-500">Loading carrier details...</p>
        </div>
      </ResponsiveLayout>
    );
  }

  if (!carrier) {
    return (
      <ResponsiveLayout 
        title="Carrier Not Found" 
        subtitle="The requested carrier could not be found"
        breadcrumbs={[{ label: "Carriers", href: "/carriers" }, { label: "Not Found" }]}
      >
        <div className="flex flex-col items-center justify-center py-16 space-y-4">
          <p className="text-gray-500">Carrier not found</p>
        </div>
      </ResponsiveLayout>
    );
  }

  return (
    <ResponsiveLayout 
      title={carrier.name || "Carrier Details"} 
      subtitle={carrier.docketNumber ? `${carrier.docketPrefix}-${carrier.docketNumber}` : "Carrier Information"}
      breadcrumbs={[
        { label: "Carriers", href: "/carriers" },
        { label: carrier.name || "Carrier" }
      ]}
    >
      <div className="space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <Truck className="w-6 h-6 text-primary" />
            <div>
              <h1 className="text-2xl font-bold" data-testid="text-carrier-name">{carrier.name}</h1>
              <p className="text-sm text-muted-foreground">
                {carrier.docketNumber ? `${carrier.docketPrefix}-${carrier.docketNumber}` : "No docket number"}
              </p>
              <AuditInfo
                createdAt={carrier.createdAt}
                createdBy={carrier.createdByUser}
                updatedAt={carrier.updatedAt}
                updatedBy={carrier.updatedByUser}
              />
            </div>
          </div>
          <div className="flex flex-col items-end gap-1">
            <div className="flex items-center gap-3">
              <Badge className={getFMCSAStatusColor(carrier.commonAuthorityStatus)} data-testid="badge-authority-status">
                {formatFMCSAStatus(carrier.commonAuthorityStatus)}
              </Badge>
              <Button 
                variant="outline" 
                onClick={() => setShowFMCSARefreshModal(true)} 
                data-testid="button-refresh-fmcsa"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh FMCSA Data
              </Button>
              <Button variant="outline" onClick={() => setShowEditModal(true)} data-testid="button-edit-carrier">
                <Edit className="w-4 h-4 mr-2" />
                Edit Carrier
              </Button>
            </div>
            {carrier.lastFmcsaFetchedAt && (
              <p className="text-xs text-muted-foreground text-right">
                FMCSA data last refreshed {format(new Date(carrier.lastFmcsaFetchedAt), "MMM d, yyyy 'at' h:mm a")}
              </p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Carrier Information</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <Truck className="w-5 h-5 text-gray-500" />
                      <div>
                        <p className="font-medium">{carrier.name}</p>
                        <p className="text-sm text-gray-500">Carrier Name</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Mail className="w-5 h-5 text-gray-500" />
                      <div>
                        <p className="font-medium">{carrier.email || "Not provided"}</p>
                        <p className="text-sm text-gray-500">Email</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Phone className="w-5 h-5 text-gray-500" />
                      <div>
                        <p className="font-medium">{formatPhoneNumber(carrier.phone) || "Not provided"}</p>
                        <p className="text-sm text-gray-500">Phone</p>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <MapPin className="w-5 h-5 text-gray-500 mt-0.5" />
                      <div>
                        <p className="font-medium">{carrier.address || "Not provided"}</p>
                        <p className="text-sm text-gray-500">Address</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Building2 className="w-5 h-5 text-gray-500" />
                      <div>
                        {carrier.company?.id ? (
                          <Link href={`/companies/${carrier.company.id}`} className="font-medium text-primary hover:underline">
                            {carrier.company.name}
                          </Link>
                        ) : (
                          <p className="font-medium text-muted-foreground">Not associated</p>
                        )}
                        <p className="text-sm text-gray-500">Company</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-gray-500" />
                      <div className="flex-1">
                        <p className="font-medium">{carrier.company?.dotProfile?.dotNumber || "Not set"}</p>
                        <p className="text-sm text-gray-500">DOT Number</p>
                      </div>
                      {carrier.docketNumber && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewProfile(carrier.docketPrefix, carrier.docketNumber || "")}
                        >
                          <ExternalLink className="w-4 h-4 mr-2" />
                          View FMCSA Profile
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div>
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Contacts</CardTitle>
              </CardHeader>
              <CardContent>
                {carrier.company?.id ? (
                  <ContactsSection
                    entityType="company"
                    entityId={carrier.company.id.toString()}
                    title=""
                  />
                ) : (
                  <p className="text-gray-500 text-center py-4">No company associated with this carrier</p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Work Orders</CardTitle>
                <Button 
                  size="sm" 
                  onClick={() => setShowCreateWorkOrderModal(true)}
                  data-testid="button-create-work-order"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Work Order
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {!primaryContact && (
                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      No primary contact is set for this carrier's company. Work orders require a requesting contact. 
                      Please set a primary contact first.
                    </AlertDescription>
                  </Alert>
                )}
                <div className="flex items-center gap-4">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <Input
                      placeholder="Search work orders..."
                      value={workOrderSearchTerm}
                      onChange={(e) => setWorkOrderSearchTerm(e.target.value)}
                      className="pl-10"
                      data-testid="input-search-work-orders"
                    />
                  </div>
                  <Select value={workOrderStatusFilter} onValueChange={setWorkOrderStatusFilter}>
                    <SelectTrigger className="w-40" data-testid="select-status-filter">
                      <SelectValue placeholder="Filter by status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="active">Active Only</SelectItem>
                      <SelectItem value="new">New</SelectItem>
                      <SelectItem value="collecting_inputs">Collecting Inputs</SelectItem>
                      <SelectItem value="validating">Validating</SelectItem>
                      <SelectItem value="awaiting_payment">Awaiting Payment</SelectItem>
                      <SelectItem value="preparing_documents">Preparing Documents</SelectItem>
                      <SelectItem value="awaiting_signatures">Awaiting Signatures</SelectItem>
                      <SelectItem value="submitted">Submitted</SelectItem>
                      <SelectItem value="awaiting_client_filing">Awaiting Client Filing</SelectItem>
                      <SelectItem value="work_complete">Work Complete</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="max-h-64 overflow-y-auto">
                  {filteredWorkOrders.length > 0 ? (
                    <div className="space-y-2">
                      {filteredWorkOrders.map((workOrder: any) => (
                        <div key={workOrder.id} className="p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <button
                                onClick={() => setLocation(`/work-orders/${workOrder.id}`)}
                                className="font-medium text-primary hover:text-primary/80 hover:underline text-left"
                                data-testid={`link-work-order-${workOrder.id}`}
                              >
                                {workOrder.workOrderNumber}
                              </button>
                              <p className="text-sm text-gray-500 mt-1">
                                {workOrder.serviceTypeName || workOrder.serviceType?.replace(/_/g, " ").replace(/\b\w/g, (l: string) => l.toUpperCase())}
                              </p>
                              <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                                <div className="flex items-center gap-1">
                                  <Calendar className="w-3 h-3" />
                                  <span>Created: {workOrder.createdAt ? format(new Date(workOrder.createdAt), 'MMM d, yyyy') : 'N/A'}</span>
                                </div>
                              </div>
                            </div>
                            <Badge className={workOrder.status === "completed" ? "bg-green-100 text-green-800" : workOrder.status === "cancelled" ? "bg-gray-100 text-gray-800" : "bg-yellow-100 text-yellow-800"}>
                              {workOrder.status?.replace(/_/g, " ").replace(/\b\w/g, (l: string) => l.toUpperCase())}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-4">
                      {carrier.workOrders && carrier.workOrders.length > 0 ? "No work orders match your search" : "No work orders found"}
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          <NotesSection
            entityType="carrier"
            entityId={carrierId?.toString() || ""}
            title="Carrier Notes"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Authority Status
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="space-y-3">
                  <h4 className="font-medium text-sm text-muted-foreground">Authority Types</h4>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Common Authority:</span>
                      <Badge className={getFMCSAStatusColor(carrier.commonAuthorityStatus)}>
                        {formatFMCSAStatus(carrier.commonAuthorityStatus)}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Contract Authority:</span>
                      <Badge className={getFMCSAStatusColor(carrier.contractAuthorityStatus)}>
                        {formatFMCSAStatus(carrier.contractAuthorityStatus)}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Broker Authority:</span>
                      <Badge className={getFMCSAStatusColor(carrier.brokerAuthorityStatus)}>
                        {formatFMCSAStatus(carrier.brokerAuthorityStatus)}
                      </Badge>
                    </div>
                  </div>
                </div>
                
                <Separator />
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <h4 className="font-medium text-sm text-muted-foreground">Pending Applications</h4>
                    <div className="space-y-1 text-sm">
                      <div className="flex items-center justify-between">
                        <span>Common:</span>
                        <Badge className={carrier.commonApplicationPending ? "bg-yellow-100 text-yellow-800" : "bg-gray-100 text-gray-500"} variant="outline">
                          {carrier.commonApplicationPending ? "Yes" : "No"}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between">
                        <span>Contract:</span>
                        <Badge className={carrier.contractApplicationPending ? "bg-yellow-100 text-yellow-800" : "bg-gray-100 text-gray-500"} variant="outline">
                          {carrier.contractApplicationPending ? "Yes" : "No"}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between">
                        <span>Broker:</span>
                        <Badge className={carrier.brokerApplicationPending ? "bg-yellow-100 text-yellow-800" : "bg-gray-100 text-gray-500"} variant="outline">
                          {carrier.brokerApplicationPending ? "Yes" : "No"}
                        </Badge>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <h4 className="font-medium text-sm text-muted-foreground">Revocation Status</h4>
                    <div className="space-y-1 text-sm">
                      <div className="flex items-center justify-between">
                        <span>Common:</span>
                        <Badge className={carrier.commonRevocationPending ? "bg-red-100 text-red-800" : "bg-gray-100 text-gray-500"} variant="outline">
                          {carrier.commonRevocationPending ? "Pending" : "None"}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between">
                        <span>Contract:</span>
                        <Badge className={carrier.contractRevocationPending ? "bg-red-100 text-red-800" : "bg-gray-100 text-gray-500"} variant="outline">
                          {carrier.contractRevocationPending ? "Pending" : "None"}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between">
                        <span>Broker:</span>
                        <Badge className={carrier.brokerRevocationPending ? "bg-red-100 text-red-800" : "bg-gray-100 text-gray-500"} variant="outline">
                          {carrier.brokerRevocationPending ? "Pending" : "None"}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </div>
                
                <Separator />
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <h4 className="font-medium text-sm text-muted-foreground">Operating Authority</h4>
                    <div className="space-y-1 text-sm">
                      <div className="flex items-center justify-between">
                        <span>Property:</span>
                        <Badge className={carrier.propertyCarriage ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-500"} variant="outline">
                          {carrier.propertyCarriage ? "Yes" : "No"}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between">
                        <span>Passenger:</span>
                        <Badge className={carrier.passengerCarriage ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-500"} variant="outline">
                          {carrier.passengerCarriage ? "Yes" : "No"}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between">
                        <span>Household:</span>
                        <Badge className={carrier.householdGoodsCarriage ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-500"} variant="outline">
                          {carrier.householdGoodsCarriage ? "Yes" : "No"}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between">
                        <span>Private:</span>
                        <Badge className={carrier.privateCarriage ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-500"} variant="outline">
                          {carrier.privateCarriage ? "Yes" : "No"}
                        </Badge>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <h4 className="font-medium text-sm text-muted-foreground">Insurance Status</h4>
                    <div className="space-y-1 text-sm">
                      <div className="flex items-center justify-between">
                        <span>BIPD Req:</span>
                        <span className="font-medium text-xs">{carrier.bipdRequired || "N/A"}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span>BIPD File:</span>
                        <span className="font-medium text-xs">{carrier.bipdOnFile || "N/A"}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span>Cargo Req:</span>
                        <span className="font-medium text-xs">{carrier.cargoRequired || "N/A"}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span>Cargo File:</span>
                        <span className="font-medium text-xs">{carrier.cargoOnFile || "N/A"}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <History className="w-5 h-5" />
                Authority History
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="max-h-80 overflow-y-auto">
                {isLoadingHistory ? (
                  <p className="text-gray-500 text-center py-4">Loading authority history...</p>
                ) : sortedAuthorityHistory.length > 0 ? (
                  <div className="space-y-3">
                    {sortedAuthorityHistory.map((event, index) => (
                      <div key={index} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                        <div className="mt-0.5">
                          {getActionIcon(event.disp_action_desc || event.original_action_desc)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge className={getActionColor(event.original_action_desc)}>
                              {event.original_action_desc}
                            </Badge>
                            {event.disp_action_desc && (
                              <>
                                <span className="text-gray-400">→</span>
                                <Badge className={getActionColor(event.disp_action_desc)}>
                                  {event.disp_action_desc}
                                </Badge>
                              </>
                            )}
                          </div>
                          <p className="text-sm font-medium mt-1">{event.mod_col_1}</p>
                          <div className="flex items-center gap-4 mt-1 text-xs text-gray-500">
                            <span>Served: {event.orig_served_date}</span>
                            {event.disp_served_date && (
                              <span>Disposition: {event.disp_served_date}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4">
                    {carrier.docketNumber ? "No authority history found" : "Enter docket number to view authority history"}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Authority Letters Section */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Authority Letters
              </CardTitle>
              {authorityLetters.length > 0 && (
                <Link href={`/authority-letters?carrierId=${carrierId}`}>
                  <Button variant="outline" size="sm" data-testid="link-view-all-letters">
                    View All
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {authorityLetters.length > 0 ? (
              <div className="space-y-3 max-h-80 overflow-y-auto">
                {authorityLetters.slice(0, 5).map((letter: any) => (
                  <div key={letter.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border">
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                      <Badge className={
                        letter.letterType === "Authority Grant" ? "bg-green-100 text-green-800 border-green-200" :
                        letter.letterType === "Reinstatement" ? "bg-blue-100 text-blue-800 border-blue-200" :
                        letter.letterType === "Name Change" ? "bg-purple-100 text-purple-800 border-purple-200" :
                        letter.letterType === "Transfer" ? "bg-amber-100 text-amber-800 border-amber-200" :
                        "bg-gray-100 text-gray-800 border-gray-200"
                      }>
                        {letter.letterType || "Unknown"}
                      </Badge>
                      {letter.authorityType && (
                        <span className="text-xs bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 px-2 py-0.5 rounded">
                          {letter.authorityType === 'A' ? 'CMO' : 
                           letter.authorityType === 'B' ? 'Contract' : 
                           letter.authorityType === 'C' ? 'Broker' : letter.authorityType}
                        </span>
                      )}
                      <div className="flex flex-col min-w-0">
                        <span className="font-medium text-sm">{letter.docketFull || "-"}</span>
                        {letter.usdotNumber && (
                          <span className="text-xs text-muted-foreground">DOT: {letter.usdotNumber}</span>
                        )}
                      </div>
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {formatDateOnly(letter.serviceDate, "MMM d, yyyy")}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      {letter.workOrder && (
                        <Link href={`/work-orders/${letter.workOrder.id}`} onClick={(e) => e.stopPropagation()}>
                          <Badge variant="outline" className="text-xs cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700">
                            WO #{letter.workOrder.id}
                          </Badge>
                        </Link>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={async () => {
                          try {
                            const response = await fetch(`/api/authority-letters/${letter.id}/download`, { credentials: "include" });
                            if (!response.ok) throw new Error("Download failed");
                            const blob = await response.blob();
                            const url = window.URL.createObjectURL(blob);
                            const a = document.createElement("a");
                            a.href = url;
                            a.download = `authority-letter-${letter.docketFull || letter.id}.pdf`;
                            document.body.appendChild(a);
                            a.click();
                            a.remove();
                            window.URL.revokeObjectURL(url);
                          } catch (error) {
                            toast({ title: "Error", description: "Failed to download letter", variant: "destructive" });
                          }
                        }}
                        title="Download letter"
                        data-testid={`button-download-${letter.id}`}
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
                {authorityLetters.length > 5 && (
                  <p className="text-sm text-muted-foreground text-center pt-2">
                    +{authorityLetters.length - 5} more letters
                  </p>
                )}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">No authority letters found</p>
            )}
          </CardContent>
        </Card>
      </div>
      
      {showEditModal && (
        <EditCarrierModal
          open={showEditModal}
          onClose={() => setShowEditModal(false)}
          onSubmit={(data) => updateCarrierMutation.mutate(data)}
          isLoading={updateCarrierMutation.isPending}
          carrier={carrier}
          companies={companies || []}
        />
      )}

      {showCreateWorkOrderModal && (
        <CreateWorkOrderModal
          onClose={() => setShowCreateWorkOrderModal(false)}
          onSubmit={(data) => createWorkOrderMutation.mutate(data)}
          isLoading={createWorkOrderMutation.isPending}
          users={users}
          currentUserId={currentUser?.id}
          prePopulate={
            primaryContact && carrier?.company?.id
              ? { 
                  contactId: primaryContact.id,
                  contactName: `${primaryContact.firstName} ${primaryContact.lastName}`,
                  companyId: carrier.company.id,
                  carrierId: carrierId || undefined,
                }
              : undefined
          }
        />
      )}

      {showFMCSARefreshModal && (
        <FMCSACarrierRefreshModal
          open={showFMCSARefreshModal}
          onClose={() => setShowFMCSARefreshModal(false)}
          carrier={carrier}
          onApply={handleFMCSARefresh}
          isUpdating={updateCarrierMutation.isPending}
        />
      )}
    </ResponsiveLayout>
  );
}
